import I18n from 'i18n-js';
import { useEffect, useState, useCallback } from 'react';
import { Card, Container } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { useAppSelector } from '../../hooks';
import { LoadingState } from '../../shared/interface';
import { getUpcomingBookings, UpcomingBookingDto } from '../../service/upcoming-bookings';
import './UpcomingBookings.scss';

export const UpcomingBookings = () => {
    const tenantId = useAppSelector(state => state.settings.tenantId);
    const routeId = useAppSelector(state => state.settings.routeId);
    const [bookings, setBookings] = useState<UpcomingBookingDto[]>([]);
    const [loading, setLoading] = useState<LoadingState>(LoadingState.IDLE);
    const [error, setError] = useState<string | null>(null);

    const fetchUpcomingBookings = useCallback(async () => {
        setLoading(LoadingState.PENDING);
        setError(null);

        try {
            // Use the actual API service
            const data = await getUpcomingBookings({
                fromDate: DateTime.now().toFormat('yyyy-MM-dd'),
                toDate: DateTime.now().plus({ days: 14 }).toFormat('yyyy-MM-dd'),
                routeId: routeId || undefined  // Pass the routeId if it exists
            });

            setBookings(data);
            setLoading(LoadingState.COMPLETE);
        } catch (err) {
            console.error('Failed to load upcoming bookings:', err);
            setError(I18n.t('DefaultErrorMessage'));
            setLoading(LoadingState.FAILED);
        }
    }, [routeId]);

    useEffect(() => {
        if (tenantId) {
            fetchUpcomingBookings();
        }
    }, [tenantId, fetchUpcomingBookings]);

    const groupBookingsByDate = (bookings: UpcomingBookingDto[]) => {
        return bookings.reduce((groups, booking) => {
            const date = booking.date;
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(booking);
            return groups;
        }, {} as Record<string, UpcomingBookingDto[]>);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('sv-SE', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };


    if (loading === LoadingState.PENDING) {
        return (
            <Container fluid className="d-flex justify-content-center align-items-center" style={{ 
                minHeight: '400px',
                
            }}>
                <div className="text-center">
                    <div className="maritime-loading mb-4" style={{ position: 'relative', height: '80px' }}>
                        {/* Floating boat */}
                        <div className="float-animation" style={{
                            position: 'absolute',
                            top: '5px',
                            left: '50%',
                            fontSize: '2.5rem',
                            color: '#0d6efd',
                            zIndex: 10
                        }}>
                            🚢
                        </div>
                    </div>
                    <h4 className="text-primary">{I18n.t('::LoadingWidget')}</h4>
                </div>
            </Container>
        );
    }

    if (loading === LoadingState.FAILED) {
        return (
            <Container fluid>
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">{I18n.t('Error')}</h4>
                    <p>{error}</p>
                </div>
            </Container>
        );
    }

    const groupedBookings = groupBookingsByDate(bookings);
    // Get route name from the first booking (all bookings should have the same route name)
    const routeName = bookings.length > 0 ? bookings[0].routeName : null;
    return (
         <Container fluid className="upcoming-bookings-container">
            <div className="py-5">
                <div className="text-center">
                    <h1 className="display-3 fw-bold text-primary mb-3">
                        {routeName}
                    </h1>
                    <div className="d-flex justify-content-center align-items-center">
                        <div className="bg-white border border-primary rounded-pill px-4 py-2 shadow-sm">
                            <i className="bi bi-calendar3 me-2 text-primary"></i>
                            <span className="fw-semibold text-dark">
                                {DateTime.now().toFormat('yyyy-MM-dd')} - {DateTime.now().plus({ days: 14 }).toFormat('yyyy-MM-dd')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {Object.keys(groupedBookings).length === 0 ? (
                <div className="text-center">
                    <h2>{I18n.t('NoDataAvailableInDatatable')}</h2>
                    <p className="text-muted">{I18n.t('::NoUpcomingBookings')}</p>
                </div>
            ) : (
                Object.entries(groupedBookings).map(([date, dayBookings]) => (
                    <Card key={date} className="my-3 shadow-sm">
                        <Card.Header className="text-primary border-0 position-relative overflow-hidden" style={{ 
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 50%, #ffffff 100%)' 
                        }}>
                            <div className="position-absolute top-0 start-0 w-100 h-100 opacity-5">
                                <i className="bi bi-waves position-absolute top-50 start-25 translate-middle fs-1 text-info"></i>
                                <i className="bi bi-waves position-absolute top-50 start-75 translate-middle fs-1 text-info"></i>
                            </div>
                            <div className="d-flex align-items-center justify-content-between position-relative">
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-calendar-date me-3 fs-3 text-primary"></i>
                                    <h3 className="mb-0 fw-bold text-primary">{formatDate(date)}</h3>
                                </div>
                                
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="booking-list">
                                {/* Table Header */}
                                <div className="booking-header d-none d-md-flex p-3 border-bottom bg-light">
                                    <div className="col-md-2 fw-bold"><i className="bi bi-geo-alt me-2"></i>{I18n.t('::Route')}</div>
                                    <div className="col-md-2 fw-bold"><i className="bi bi-pin-map me-2"></i>{I18n.t('::From')}</div>
                                    <div className="col-md-2 fw-bold"><i className="bi bi-pin-map-fill me-2"></i>{I18n.t('::To')}</div>
                                    <div className="col-md-2 fw-bold"><i className="bi bi-clock me-2"></i>{I18n.t('::Departure')}</div>
                                    <div className="col-md-2 fw-bold"><i className="bi bi-clock-fill me-2"></i>{I18n.t('::Arrival2')}</div>
                                    <div className="col-md-2 fw-bold"><i className="bi bi-people me-2"></i>{I18n.t('::Passengers')}</div>
                                </div>

                                {dayBookings.map((booking, index) => {
                                    const isEven = index % 2 === 0;
                                    return (
                                        <div key={booking.id} className={`booking-row d-flex flex-column d-md-flex flex-md-row p-3 ${isEven ? 'bg-white' : 'bg-light'}`}>
                                            {/* Mobile Layout */}
                                            <div className="d-md-none w-100">
                                                <div className="booking-field mb-2 text-start">
                                                    <div className="field-label fw-bold text-muted small text-start">
                                                        <i className="bi bi-geo-alt me-2 text-primary"></i>{I18n.t('::Route')}
                                                    </div>
                                                    <div className="field-value text-start">{booking.routeName}</div>
                                                </div>
                                                <div className="booking-field mb-2 text-start">
                                                    <div className="field-label fw-bold text-muted small text-start">
                                                        <i className="bi bi-pin-map me-2 text-success"></i>{I18n.t('::From')}
                                                    </div>
                                                    <div className="field-value text-start">{booking.departureHarbour}</div>
                                                </div>
                                                <div className="booking-field mb-2 text-start">
                                                    <div className="field-label fw-bold text-muted small text-start">
                                                        <i className="bi bi-pin-map-fill me-2 text-danger"></i>{I18n.t('::To')}
                                                    </div>
                                                    <div className="field-value text-start">{booking.arrivalHarbour}</div>
                                                </div>
                                                <div className="booking-field mb-2 text-start">
                                                    <div className="field-label fw-bold text-muted small text-start">
                                                        <i className="bi bi-clock me-2 text-info"></i>{I18n.t('::Departure')}
                                                    </div>
                                                    <div className="field-value text-start">{booking.departureTime}</div>
                                                </div>
                                                <div className="booking-field mb-2 text-start">
                                                    <div className="field-label fw-bold text-muted small text-start">
                                                        <i className="bi bi-clock-fill me-2 text-warning"></i>{I18n.t('::Arrival2')}
                                                    </div>
                                                    <div className="field-value text-start">{booking.arrivalTime}</div>
                                                </div>
                                                <div className="booking-field mb-2 text-start">
                                                    <div className="field-label fw-bold text-muted small text-start">
                                                        <i className="bi bi-people me-2 text-secondary"></i>{I18n.t('::Passengers')}
                                                    </div>
                                                    <div className="field-value text-start">
                                                        <i className="bi bi-person-check me-1 text-success"></i>
                                                        {booking.bookedSeats}/{booking.capacity}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Desktop Layout */}
                                            <div className="col-md-2 d-none d-md-block">
                                                <i className="bi bi-ship me-2 text-primary"></i>{booking.routeName}
                                            </div>
                                            <div className="col-md-2 d-none d-md-block">
                                                <i className="bi bi-geo-alt me-2 text-success"></i>{booking.departureHarbour}
                                            </div>
                                            <div className="col-md-2 d-none d-md-block">
                                                <i className="bi bi-geo-alt-fill me-2 text-danger"></i>{booking.arrivalHarbour}
                                            </div>
                                            <div className="col-md-2 d-none d-md-block">
                                                <i className="bi bi-play-circle me-2 text-info"></i>{booking.departureTime}
                                            </div>
                                            <div className="col-md-2 d-none d-md-block">
                                                <i className="bi bi-stop-circle me-2 text-warning"></i>{booking.arrivalTime}
                                            </div>
                                            <div className="col-md-2 d-none d-md-block">
                                                <i className="bi bi-people-fill me-2 text-secondary"></i>
                                                {booking.bookedSeats}/{booking.capacity}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card.Body>
                    </Card>
                ))
            )}
        </Container>
    );
};
